import type { CalendarDay, Config, Contacto, Reserva, Service } from "@/types";
import { DOW_SHORT, MONTHS_SHORT, digitsOnly, uid } from "./format";

/** Calendario de los próximos `days` días desde `today`, marcando los días cerrados. */
export function buildCalendar(
  today: Date,
  days: number,
  closedWeekdays: number[],
): CalendarDay[] {
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const result: CalendarDay[] = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    result.push({
      iso: toISODate(d),
      dow: DOW_SHORT[d.getDay()],
      day: d.getDate(),
      month: MONTHS_SHORT[d.getMonth()],
      closed: closedWeekdays.includes(d.getDay()),
    });
  }
  return result;
}

/** "YYYY-MM-DD" de una Date local (sin el corrimiento de toISOString en UTC). */
export function toISODate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Franjas ya ocupadas para una fecha (excluye canceladas). */
export function takenTimes(reservas: Reserva[], date: string): string[] {
  return reservas
    .filter((r) => r.date === date && r.status !== "cancelada")
    .map((r) => r.time);
}

/** Crea una reserva confirmada. */
export function createReserva(
  service: Service,
  date: string,
  time: string,
  name: string,
  phone: string,
  note: string,
): Reserva {
  return {
    id: uid(),
    service: service.name,
    serviceId: service.id,
    price: service.price,
    date,
    time,
    name,
    phone,
    note,
    status: "confirmada",
    createdAt: Date.now(),
  };
}

/** Cancela una reserva por id (devuelve una lista nueva). */
export function cancelReserva(list: Reserva[], id: string): Reserva[] {
  return list.map((r) => (r.id === id ? { ...r, status: "cancelada" as const } : r));
}

/** Reservas no canceladas. */
export function activeReservas(list: Reserva[]): Reserva[] {
  return list.filter((r) => r.status !== "cancelada");
}

/** Total agendado (incluye canceladas, como en el panel histórico). */
export function totalAgendado(list: Reserva[]): number {
  return list.reduce((acc, r) => acc + (r.price || 0), 0);
}

/** Busca un contacto por teléfono (ignorando formatos). */
export function findContactoByPhone(
  contactos: Contacto[],
  phone: string,
): Contacto | undefined {
  const target = digitsOnly(phone);
  if (!target) return undefined;
  return contactos.find((c) => digitsOnly(c.phone) === target);
}

/** Registra una sesión en el historial del contacto (si existe). */
export function appendHistory(
  contactos: Contacto[],
  phone: string,
  entry: string,
): Contacto[] {
  const target = digitsOnly(phone);
  if (!target) return contactos;
  return contactos.map((c) =>
    digitsOnly(c.phone) === target
      ? { ...c, history: [...(c.history ?? []), entry] }
      : c,
  );
}

/** Entrada de historial: "2026-03-12 15:00 · Semipermanente". */
export function historyEntry(reserva: Reserva): string {
  return `${reserva.date} ${reserva.time} · ${reserva.service}`;
}

/** Servicios destacados (home) o, si no hay coincidencia, los sugeridos por defecto. */
export function featuredServices(services: Service[], featuredIds: string[]): Service[] {
  const found = featuredIds
    .map((id) => services.find((s) => s.id === id))
    .filter((s): s is Service => Boolean(s));
  return found.length > 0 ? found : services.slice(0, 4);
}

/** Servicios de una categoría. */
export function byCategory(services: Service[], cat: string): Service[] {
  return services.filter((s) => s.cat === cat);
}

/** Reserva activa de un servicio concreto. */
export function findService(services: Service[], id: string | null): Service | undefined {
  if (!id) return undefined;
  return services.find((s) => s.id === id);
}

/** ¿Está cerrado ese día de la semana? */
export function isClosed(date: Date, config: Pick<Config, "closedWeekdays">): boolean {
  return config.closedWeekdays.includes(date.getDay());
}
